/**
 * Konva 노드 핸들(INode 계열 구현).
 *
 * - 핸들은 Konva 노드당 정확히 1개(WeakMap). `handleOf(node)`는 같은 노드에 항상 같은 핸들을 돌려주므로
 *   엔진의 참조 비교(`e.target === stage`, `entity.getParent() === holder`, Transformer nodes() 유효성)가 유지된다.
 * - 접근자는 Konva와 같이 `arguments.length`로 getter/setter를 가른다(`x(undefined)`는 Konva에서 기본값 복원 setter).
 * - `unwrap(handle)`은 backend/konva 안에서만 쓴다.
 */
import Konva from 'konva'
import type { Context } from 'konva/lib/Context'
import type { Shape, ShapeConfig as KonvaShapeConfig } from 'konva/lib/Shape'
import type { Transform } from 'konva/lib/Util'
import type {
  ClientRectOptions,
  IArrow,
  ICircle,
  IContainer,
  IEllipse,
  IGroup,
  IImage,
  ILayer,
  ILine,
  IMatrix,
  INode,
  IPath,
  IPathContext,
  IRect,
  IRing,
  IShape,
  IStage,
  IText,
  PointerEventHandler,
} from '../IRenderer'
import type {
  HitArea,
  LineCap,
  LineJoin,
  NodeKind,
  NodeTag,
  Rect,
  Size,
  TextAlign,
  TextVerticalAlign,
  Vector2d,
} from '../types'
import { toKonvaListener } from './KonvaEvents'

const HANDLES = new WeakMap<Konva.Node, KonvaNodeHandle>()

/** Konva 노드의 핸들(노드당 1개, 없으면 종류에 맞게 생성) */
export function handleOf(node: Konva.Stage): IStage
export function handleOf(node: Konva.Layer): ILayer
export function handleOf(node: Konva.Group): IGroup
export function handleOf(node: Konva.Node): INode
export function handleOf(node: Konva.Node): INode {
  // 이미 핸들이면 그대로(경계 마이그레이션 중 핸들이 다시 감싸져 'Node' 래퍼가 생기는 것 방지)
  if (node instanceof KonvaNodeHandle) return node
  const cached = HANDLES.get(node)
  if (cached) return cached
  const handle = createHandle(node)
  HANDLES.set(node, handle)
  return handle
}

/** 핸들 → Konva 노드(마이그레이션·구현체 내부 전용) */
export function unwrap(handle: INode): Konva.Node
export function unwrap<T extends Konva.Node>(handle: INode, as: new (...a: never[]) => T): T
export function unwrap(handle: INode, as?: new (...a: never[]) => Konva.Node): Konva.Node {
  if (!(handle instanceof KonvaNodeHandle)) {
    throw new Error('[KonvaNode] Konva 렌더러의 핸들이 아닙니다')
  }
  const node = handle.konva
  if (as && !(node instanceof as)) {
    throw new Error(`[KonvaNode] 핸들의 노드가 ${as.name}이 아닙니다: ${node.getClassName()}`)
  }
  return node
}

function createHandle(node: Konva.Node): KonvaNodeHandle {
  // 순서 주의: Transformer·Layer·Stage는 Group/Container를 상속, Arrow는 Line을 상속한다.
  // getClassName()은 setAttr('className')로 바뀔 수 있으므로(풍선 표식 등) instanceof로 판정한다.
  if (node instanceof Konva.Stage) return new KonvaStageHandle(node)
  if (node instanceof Konva.Layer) return new KonvaLayerHandle(node)
  if (node instanceof Konva.Transformer) return new KonvaGroupHandle(node, 'Transformer')
  if (node instanceof Konva.Group) return new KonvaGroupHandle(node, 'Group')
  if (node instanceof Konva.Arrow) return new KonvaArrowHandle(node)
  if (node instanceof Konva.Line) return new KonvaLineHandle(node)
  if (node instanceof Konva.Rect) return new KonvaRectHandle(node)
  if (node instanceof Konva.Ellipse) return new KonvaEllipseHandle(node)
  if (node instanceof Konva.Circle) return new KonvaCircleHandle(node)
  if (node instanceof Konva.Ring) return new KonvaRingHandle(node)
  if (node instanceof Konva.Text) return new KonvaTextHandle(node)
  if (node instanceof Konva.Image) return new KonvaImageHandle(node)
  if (node instanceof Konva.Shape) {
    // 등록된 하위 클래스가 아닌 Konva.Shape(sceneFunc) = IPath
    return node.getClassName() === 'Shape' ? new KonvaPathHandle(node) : new KonvaShapeHandle(node)
  }
  if (node instanceof Konva.Container) return new KonvaContainerHandle(node, 'Node')
  return new KonvaNodeHandle(node, 'Node')
}

class KonvaMatrix implements IMatrix {
  constructor(private readonly transform: Transform) {}
  copy(): IMatrix {
    return new KonvaMatrix(this.transform.copy())
  }
  invert(): IMatrix {
    this.transform.invert()
    return this
  }
  point(p: Vector2d): Vector2d {
    return this.transform.point(p)
  }
}

export class KonvaNodeHandle<K extends Konva.Node = Konva.Node> implements INode {
  constructor(
    readonly konva: K,
    readonly kind: NodeKind,
  ) {}

  // ---- 변환·표시 ----
  x(): number
  x(v: number): this
  x(v?: number): number | this {
    if (arguments.length === 0) return this.konva.x()
    this.konva.x(v as number)
    return this
  }
  y(): number
  y(v: number): this
  y(v?: number): number | this {
    if (arguments.length === 0) return this.konva.y()
    this.konva.y(v as number)
    return this
  }
  position(): Vector2d
  position(v: Vector2d): this
  position(v?: Vector2d): Vector2d | this {
    if (arguments.length === 0) return this.konva.position()
    this.konva.position(v as Vector2d)
    return this
  }
  scaleX(): number
  scaleX(v: number): this
  scaleX(v?: number): number | this {
    if (arguments.length === 0) return this.konva.scaleX()
    this.konva.scaleX(v as number)
    return this
  }
  scaleY(): number
  scaleY(v: number): this
  scaleY(v?: number): number | this {
    if (arguments.length === 0) return this.konva.scaleY()
    this.konva.scaleY(v as number)
    return this
  }
  scale(): Vector2d
  scale(v: Vector2d): this
  scale(v?: Vector2d): Vector2d | this {
    if (arguments.length === 0) return this.konva.scale()
    this.konva.scale(v as Vector2d)
    return this
  }
  rotation(): number
  rotation(v: number): this
  rotation(v?: number): number | this {
    if (arguments.length === 0) return this.konva.rotation()
    this.konva.rotation(v as number)
    return this
  }
  rotate(deg: number): this {
    this.konva.rotate(deg)
    return this
  }
  offsetX(): number
  offsetX(v: number): this
  offsetX(v?: number): number | this {
    if (arguments.length === 0) return this.konva.offsetX()
    this.konva.offsetX(v as number)
    return this
  }
  offsetY(): number
  offsetY(v: number): this
  offsetY(v?: number): number | this {
    if (arguments.length === 0) return this.konva.offsetY()
    this.konva.offsetY(v as number)
    return this
  }
  opacity(): number
  opacity(v: number): this
  opacity(v?: number): number | this {
    if (arguments.length === 0) return this.konva.opacity()
    this.konva.opacity(v as number)
    return this
  }
  visible(): boolean
  visible(v: boolean): this
  visible(v?: boolean): boolean | this {
    if (arguments.length === 0) return this.konva.visible()
    this.konva.visible(v as boolean)
    return this
  }
  show(): this {
    this.konva.show()
    return this
  }
  hide(): this {
    this.konva.hide()
    return this
  }
  width(): number
  width(v: number): this
  width(v?: number): number | this {
    if (arguments.length === 0) return this.konva.width()
    this.konva.width(v as number)
    return this
  }
  height(): number
  height(v: number): this
  height(v?: number): number | this {
    if (arguments.length === 0) return this.konva.height()
    this.konva.height(v as number)
    return this
  }
  size(): Size
  size(v: Size): this
  size(v?: Size): Size | this {
    if (arguments.length === 0) return this.konva.size()
    this.konva.size(v as Size)
    return this
  }

  // ---- 계층·z ----
  getParent(): IContainer | null {
    const parent = this.konva.getParent()
    return parent ? (handleOf(parent) as IContainer) : null
  }
  getLayer(): ILayer | null {
    const layer = this.konva.getLayer()
    return layer ? handleOf(layer) : null
  }
  getStage(): IStage | null {
    const stage = this.konva.getStage()
    return stage ? handleOf(stage) : null
  }
  zIndex(): number
  zIndex(v: number): this
  zIndex(v?: number): number | this {
    if (arguments.length === 0) return this.konva.zIndex()
    this.konva.zIndex(v as number)
    return this
  }
  moveToTop(): boolean {
    return this.konva.moveToTop()
  }
  moveToBottom(): boolean {
    return this.konva.moveToBottom()
  }
  remove(): this {
    this.konva.remove()
    return this
  }
  destroy(): this {
    this.konva.destroy()
    return this
  }

  // ---- 식별·사용자 데이터 ----
  id(): string
  id(v: string): this
  id(v?: string): string | this {
    if (arguments.length === 0) return this.konva.id()
    this.konva.id(v as string)
    return this
  }
  name(): string
  name(v: string): this
  name(v?: string): string | this {
    if (arguments.length === 0) return this.konva.name()
    this.konva.name(v as string)
    return this
  }
  getAttr(key: string): unknown {
    return this.konva.getAttr(key)
  }
  setAttr(key: string, v: unknown): this {
    this.konva.setAttr(key, v)
    return this
  }

  // ---- 입력 ----
  listening(): boolean
  listening(v: boolean): this
  listening(v?: boolean): boolean | this {
    if (arguments.length === 0) return this.konva.listening()
    this.konva.listening(v as boolean)
    return this
  }
  draggable(): boolean
  draggable(v: boolean): this
  draggable(v?: boolean): boolean | this {
    if (arguments.length === 0) return this.konva.draggable()
    this.konva.draggable(v as boolean)
    return this
  }
  dragBoundFunc(fn?: (pos: Vector2d) => Vector2d): this {
    // Konva는 dragBoundFunc를 (pos, evt) 시그니처로 부르고 this를 노드로 묶는다. 인자 없으면 해제
    this.konva.dragBoundFunc(fn as unknown as (this: Konva.Node, pos: Vector2d) => Vector2d)
    return this
  }
  isDragging(): boolean {
    return this.konva.isDragging()
  }
  startDrag(): void {
    this.konva.startDrag()
  }
  stopDrag(): void {
    this.konva.stopDrag()
  }
  cancelDrag(): void {
    // Konva.DD._dragElements에는 대기('ready')·진행('dragging') 드래그가 모두 있다 — stopDrag가 둘 다 지운다
    if (Konva.DD._dragElements.has(this.konva._id)) this.konva.stopDrag()
  }
  on(evt: string, handler: PointerEventHandler): this {
    this.konva.on(evt, toKonvaListener(handler))
    return this
  }
  off(evt: string): this {
    this.konva.off(evt)
    return this
  }
  fire(evt: string, bubble: boolean = false): this {
    this.konva.fire(evt, undefined, bubble)
    return this
  }

  // ---- 기하 ----
  getClientRect(opts?: ClientRectOptions): Rect {
    if (!opts) return this.konva.getClientRect()
    return this.konva.getClientRect({
      skipTransform: opts.skipTransform,
      skipStroke: opts.skipStroke,
      relativeTo: opts.relativeTo ? (unwrap(opts.relativeTo) as Konva.Container) : undefined,
    })
  }
  getAbsolutePosition(): Vector2d {
    return this.konva.getAbsolutePosition()
  }
  getAbsoluteTransform(): IMatrix {
    return new KonvaMatrix(this.konva.getAbsoluteTransform())
  }
  getAbsoluteRotation(): number {
    return this.konva.getAbsoluteRotation()
  }
}

export class KonvaContainerHandle<K extends Konva.Container = Konva.Container>
  extends KonvaNodeHandle<K>
  implements IContainer
{
  add(...nodes: INode[]): this {
    // Konva.Container.add는 가변 인자를 받아 순서대로 붙인다(엔티티 생성자의 조립 순서 = 캐시·hit 순서)
    this.konva.add(...nodes.map(n => unwrap(n)))
    return this
  }
  getChildren(filter?: (n: INode) => boolean): INode[] {
    const children = filter
      ? this.konva.getChildren((node: Konva.Node) => filter(handleOf(node)))
      : this.konva.getChildren()
    return children.map(node => handleOf(node))
  }
  find<T extends INode = INode>(selector: NodeTag | ((n: INode) => boolean)): T[] {
    const found =
      typeof selector === 'function'
        ? this.konva.find((node: Konva.Node) => selector(handleOf(node)))
        : this.konva.find(selector)
    return found.map(node => handleOf(node) as unknown as T)
  }
  removeChildren(): this {
    this.konva.removeChildren()
    return this
  }
  destroyChildren(): this {
    this.konva.destroyChildren()
    return this
  }
  clip(rect: Rect | null): this {
    if (rect) {
      this.konva.clip(rect)
    } else {
      this.konva.clipX(undefined as unknown as number)
      this.konva.clipY(undefined as unknown as number)
      this.konva.clipWidth(undefined as unknown as number)
      this.konva.clipHeight(undefined as unknown as number)
    }
    return this
  }
}

export class KonvaGroupHandle extends KonvaContainerHandle<Konva.Group> implements IGroup {
  declare readonly kind: 'Group' | 'Transformer'
}

export class KonvaLayerHandle extends KonvaContainerHandle<Konva.Layer> implements ILayer {
  declare readonly kind: 'Layer'
  constructor(layer: Konva.Layer) {
    super(layer, 'Layer')
  }
  batchDraw(): this {
    // withoutAutoDraw 구간(자동 그리기 꺼짐)에서는 엔티티 코드의 명시 예약도 생략 — 호출 측이 끝에 한 번 그린다
    // (층 진입 조각 생성 중 풍선 배치의 batchDraw가 조각마다 미완성 레이어를 그렸다)
    if (!Konva.autoDrawEnabled) return this
    this.konva.batchDraw()
    return this
  }
  draw(): this {
    this.konva.draw()
    return this
  }
}

export class KonvaStageHandle extends KonvaContainerHandle<Konva.Stage> implements IStage {
  declare readonly kind: 'Stage'
  constructor(stage: Konva.Stage) {
    super(stage, 'Stage')
  }
  container(): HTMLDivElement {
    return this.konva.container()
  }
  getLayers(): ILayer[] {
    return this.konva.getLayers().map(layer => handleOf(layer))
  }
  setViewTransform(scale: number, pos: Vector2d): this {
    // setAttrs는 Konva _batchTransformChanges로 묶인다. 키는 평평하게(x·y) — setPosition은 자체 배치가
    // 안쪽에서 배치를 닫아(재진입 불가) 뒤따르는 setter가 다시 바로 순회한다
    this.konva.setAttrs({ scaleX: scale, scaleY: scale, x: pos.x, y: pos.y })
    return this
  }
}

/**
 * HitArea → Konva hitFunc/hitStrokeWidth 번역. 각 분기는 엔진 원본 hitFunc(pointEntity·rectEntity·brushEntity)과
 * 같은 Context 호출 순서를 유지한다.
 * 주의(Konva HitContext._stroke): hit 캔버스 stroke 굵기는 hitFunc 안의 `context.lineWidth` 대입이 아니라
 * `hitStrokeWidth()`('auto'면 strokeWidth())로 결정된다. strokeScale 분기의 lineWidth 대입은 원본과 동일하게
 * 유지하지만 실제 hit 굵기는 hitStrokeWidth가 좌우한다(브러시는 entityFactory가 +50을 따로 지정).
 */
function applyHitArea(shape: Shape<KonvaShapeConfig>, spec: HitArea): void {
  switch (spec.kind) {
    case 'default':
      shape.hitFunc(undefined as unknown as (ctx: Context, shape: Shape) => void)
      shape.hitStrokeWidth('auto')
      return
    case 'circle': {
      const radius = spec.radius
      shape.hitFunc((context: Context, self: Shape<KonvaShapeConfig>): void => {
        context.beginPath()
        context.arc(0, 0, radius, 0, Math.PI * 2)
        context.closePath()
        context.fillStrokeShape(self)
      })
      return
    }
    case 'rectPad': {
      const padding = spec.pad
      shape.hitStrokeWidth(padding)
      shape.hitFunc((context: Context, self: Shape<KonvaShapeConfig>): void => {
        context.beginPath()
        context.rect(-padding, -padding, self.width() + padding * 2, self.height() + padding * 2)
        context.closePath()
        context.fillStrokeShape(self)
      })
      return
    }
    case 'strokeScale': {
      const scale = spec.scale
      const base = spec.baseStrokeWidth
      shape.hitFunc((context: Context, self: Shape<KonvaShapeConfig>): void => {
        const line = self as Konva.Line
        const points = typeof line.points === 'function' ? line.points() : []
        const strokeWidth = (base ?? self.strokeWidth()) * scale
        context.beginPath()
        context.moveTo(points[0], points[1])
        for (let i = 2; i < points.length; i += 2) {
          context.lineTo(points[i], points[i + 1])
        }
        context.lineWidth = strokeWidth
        context.strokeShape(self)
      })
      return
    }
    case 'strokeWidth':
      shape.hitStrokeWidth(spec.px)
      return
  }
}

export class KonvaShapeHandle<K extends Konva.Shape = Konva.Shape>
  extends KonvaNodeHandle<K>
  implements IShape
{
  constructor(shape: K, kind: NodeKind = 'Shape') {
    super(shape, kind)
  }
  stroke(): string
  stroke(v: string | undefined): this
  stroke(v?: string): string | this {
    if (arguments.length === 0) return this.konva.stroke() as string
    this.konva.stroke(v as string)
    return this
  }
  strokeWidth(): number
  strokeWidth(v: number): this
  strokeWidth(v?: number): number | this {
    if (arguments.length === 0) return this.konva.strokeWidth()
    this.konva.strokeWidth(v as number)
    return this
  }
  fill(): string
  fill(v: string | undefined): this
  fill(v?: string): string | this {
    if (arguments.length === 0) return this.konva.fill() as string
    this.konva.fill(v as string)
    return this
  }
  dash(): number[]
  dash(v: number[] | undefined): this
  dash(v?: number[]): number[] | this {
    if (arguments.length === 0) return this.konva.dash()
    this.konva.dash(v as number[])
    return this
  }
  lineCap(): LineCap
  lineCap(v: LineCap): this
  lineCap(v?: LineCap): LineCap | this {
    if (arguments.length === 0) return this.konva.lineCap()
    this.konva.lineCap(v as LineCap)
    return this
  }
  lineJoin(): LineJoin
  lineJoin(v: LineJoin): this
  lineJoin(v?: LineJoin): LineJoin | this {
    if (arguments.length === 0) return this.konva.lineJoin()
    this.konva.lineJoin(v as LineJoin)
    return this
  }
  hitArea(spec: HitArea): this {
    applyHitArea(this.konva, spec)
    return this
  }
  fillPattern(p: { image: CanvasImageSource; offset?: Vector2d } | null): this {
    if (!p) {
      this.konva.fillPatternImage(undefined as unknown as HTMLImageElement)
      return this
    }
    this.konva.fillPatternImage(p.image as HTMLImageElement)
    if (p.offset) this.konva.fillPatternOffset(p.offset)
    return this
  }
  perfectDraw(v: boolean): this {
    this.konva.perfectDrawEnabled(v)
    return this
  }
  globalCompositeOperation(): GlobalCompositeOperation
  globalCompositeOperation(v: GlobalCompositeOperation): this
  globalCompositeOperation(v?: GlobalCompositeOperation): GlobalCompositeOperation | this {
    if (arguments.length === 0) {
      return this.konva.globalCompositeOperation() as GlobalCompositeOperation
    }
    this.konva.globalCompositeOperation(v as GlobalCompositeOperation)
    return this
  }
}

export class KonvaRectHandle extends KonvaShapeHandle<Konva.Rect> implements IRect {
  declare readonly kind: 'Rect'
  constructor(rect: Konva.Rect) {
    super(rect, 'Rect')
  }
  cornerRadius(): number
  cornerRadius(v: number): this
  cornerRadius(v?: number): number | this {
    if (arguments.length === 0) return this.konva.cornerRadius() as number
    this.konva.cornerRadius(v as number)
    return this
  }
}

export class KonvaEllipseHandle extends KonvaShapeHandle<Konva.Ellipse> implements IEllipse {
  declare readonly kind: 'Ellipse'
  constructor(ellipse: Konva.Ellipse) {
    super(ellipse, 'Ellipse')
  }
  radiusX(): number
  radiusX(v: number): this
  radiusX(v?: number): number | this {
    if (arguments.length === 0) return this.konva.radiusX()
    this.konva.radiusX(v as number)
    return this
  }
  radiusY(): number
  radiusY(v: number): this
  radiusY(v?: number): number | this {
    if (arguments.length === 0) return this.konva.radiusY()
    this.konva.radiusY(v as number)
    return this
  }
}

export class KonvaCircleHandle extends KonvaShapeHandle<Konva.Circle> implements ICircle {
  declare readonly kind: 'Circle'
  constructor(circle: Konva.Circle) {
    super(circle, 'Circle')
  }
  radius(): number
  radius(v: number): this
  radius(v?: number): number | this {
    if (arguments.length === 0) return this.konva.radius()
    this.konva.radius(v as number)
    return this
  }
}

export class KonvaRingHandle extends KonvaShapeHandle<Konva.Ring> implements IRing {
  declare readonly kind: 'Ring'
  constructor(ring: Konva.Ring) {
    super(ring, 'Ring')
  }
  innerRadius(): number
  innerRadius(v: number): this
  innerRadius(v?: number): number | this {
    if (arguments.length === 0) return this.konva.innerRadius()
    this.konva.innerRadius(v as number)
    return this
  }
  outerRadius(): number
  outerRadius(v: number): this
  outerRadius(v?: number): number | this {
    if (arguments.length === 0) return this.konva.outerRadius()
    this.konva.outerRadius(v as number)
    return this
  }
}

export class KonvaLineHandle<K extends Konva.Line = Konva.Line>
  extends KonvaShapeHandle<K>
  implements ILine
{
  declare readonly kind: 'Line' | 'Arrow'
  constructor(line: K, kind: 'Line' | 'Arrow' = 'Line') {
    super(line, kind)
  }
  points(): number[]
  points(v: number[]): this
  points(v?: number[]): number[] | this {
    if (arguments.length === 0) return this.konva.points()
    this.konva.points(v as number[])
    return this
  }
  tension(): number
  tension(v: number): this
  tension(v?: number): number | this {
    if (arguments.length === 0) return this.konva.tension()
    this.konva.tension(v as number)
    return this
  }
  closed(): boolean
  closed(v: boolean): this
  closed(v?: boolean): boolean | this {
    if (arguments.length === 0) return this.konva.closed()
    this.konva.closed(v as boolean)
    return this
  }
}

export class KonvaArrowHandle extends KonvaLineHandle<Konva.Arrow> implements IArrow {
  declare readonly kind: 'Arrow'
  constructor(arrow: Konva.Arrow) {
    super(arrow, 'Arrow')
  }
  pointerLength(): number
  pointerLength(v: number): this
  pointerLength(v?: number): number | this {
    if (arguments.length === 0) return this.konva.pointerLength()
    this.konva.pointerLength(v as number)
    return this
  }
  pointerWidth(): number
  pointerWidth(v: number): this
  pointerWidth(v?: number): number | this {
    if (arguments.length === 0) return this.konva.pointerWidth()
    this.konva.pointerWidth(v as number)
    return this
  }
}

export class KonvaTextHandle extends KonvaShapeHandle<Konva.Text> implements IText {
  declare readonly kind: 'Text'
  constructor(text: Konva.Text) {
    super(text, 'Text')
  }
  text(): string
  text(v: string): this
  text(v?: string): string | this {
    if (arguments.length === 0) return this.konva.text()
    this.konva.text(v as string)
    return this
  }
  fontSize(): number
  fontSize(v: number): this
  fontSize(v?: number): number | this {
    if (arguments.length === 0) return this.konva.fontSize()
    this.konva.fontSize(v as number)
    return this
  }
  fontFamily(): string
  fontFamily(v: string): this
  fontFamily(v?: string): string | this {
    if (arguments.length === 0) return this.konva.fontFamily()
    this.konva.fontFamily(v as string)
    return this
  }
  align(): TextAlign
  align(v: TextAlign): this
  align(v?: TextAlign): TextAlign | this {
    if (arguments.length === 0) return this.konva.align() as TextAlign
    this.konva.align(v as TextAlign)
    return this
  }
  verticalAlign(): TextVerticalAlign
  verticalAlign(v: TextVerticalAlign): this
  verticalAlign(v?: TextVerticalAlign): TextVerticalAlign | this {
    if (arguments.length === 0) return this.konva.verticalAlign() as TextVerticalAlign
    this.konva.verticalAlign(v as TextVerticalAlign)
    return this
  }
  /** Konva.Text의 width()/height()는 'auto'일 때 텍스트 측정값(+padding)을 돌려준다(balloonHead 149~150) */
  measure(): Size {
    return { width: this.konva.width(), height: this.konva.height() }
  }
}

export class KonvaImageHandle extends KonvaShapeHandle<Konva.Image> implements IImage {
  declare readonly kind: 'Image'
  constructor(image: Konva.Image) {
    super(image, 'Image')
  }
  image(): CanvasImageSource | undefined
  image(v: CanvasImageSource | undefined): this
  image(v?: CanvasImageSource): CanvasImageSource | undefined | this {
    if (arguments.length === 0) return this.konva.image()
    this.konva.image(v as CanvasImageSource)
    return this
  }
}

/** Konva Context를 IPathContext로 감싼다(fillStrokeShape/strokeShape는 자기 도형에 바인딩) */
class KonvaPathContext implements IPathContext {
  constructor(
    private readonly ctx: Context,
    private readonly shape: Konva.Shape,
  ) {}
  beginPath(): void {
    this.ctx.beginPath()
  }
  closePath(): void {
    this.ctx.closePath()
  }
  moveTo(x: number, y: number): void {
    this.ctx.moveTo(x, y)
  }
  lineTo(x: number, y: number): void {
    this.ctx.lineTo(x, y)
  }
  arc(x: number, y: number, r: number, start: number, end: number, anticlockwise?: boolean): void {
    this.ctx.arc(x, y, r, start, end, anticlockwise)
  }
  quadraticCurveTo(cpx: number, cpy: number, x: number, y: number): void {
    this.ctx.quadraticCurveTo(cpx, cpy, x, y)
  }
  rect(x: number, y: number, w: number, h: number): void {
    this.ctx.rect(x, y, w, h)
  }
  stroke(): void {
    this.ctx.stroke()
  }
  fillStrokeShape(): void {
    this.ctx.fillStrokeShape(this.shape)
  }
  strokeShape(): void {
    this.ctx.strokeShape(this.shape)
  }
  get lineWidth(): number {
    return this.ctx.lineWidth
  }
  set lineWidth(v: number) {
    this.ctx.lineWidth = v
  }
  get strokeStyle(): string {
    return this.ctx.strokeStyle as string
  }
  set strokeStyle(v: string) {
    this.ctx.strokeStyle = v
  }
  get lineCap(): LineCap {
    return this.ctx.lineCap as LineCap
  }
  set lineCap(v: LineCap) {
    this.ctx.lineCap = v
  }
  get lineJoin(): LineJoin {
    return this.ctx.lineJoin as LineJoin
  }
  set lineJoin(v: LineJoin) {
    this.ctx.lineJoin = v
  }
}

export class KonvaPathHandle extends KonvaShapeHandle<Konva.Shape> implements IPath {
  declare readonly kind: 'Path'
  constructor(shape: Konva.Shape) {
    super(shape, 'Path')
  }
  redraw(draw: (ctx: IPathContext, self: IPath) => void): this {
    this.konva.sceneFunc((context: Context, shape: Konva.Shape): void => {
      draw(new KonvaPathContext(context, shape), this)
    })
    return this
  }
}
